import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import Loader from "./Loader";
import { Link } from "react-router-dom";
import {
  HIDDEN_KEYS,
  filterVisible,
  hideItem,
} from "../utils/hiddenHistory";
import {
  formatAppDate,
  todayLocalDateString,
} from "../utils/dateFormat";

const defaultExpense = () => ({
  title: "",
  description: "",
  expense_date: todayLocalDateString(),
  amount: "",
});

const defaultTitleOptions = [
  "Milk",
  "Petrol",
  "Rent",
  "Utilities",
  "Office Supplies",
  "Other",
];

const getStoredExpenseTitles = () => {
  try {
    return JSON.parse(localStorage.getItem("expenseTitles") || "[]");
  } catch {
    return [];
  }
};

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [titleOptions, setTitleOptions] = useState(defaultTitleOptions);
  const [filterTitle, setFilterTitle] = useState("");
  const [expense, setExpense] = useState(defaultExpense());
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [listVersion, setListVersion] = useState(0);

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    setFetchError("");
    try {
      const response = await axios.get(`${API_BASE_URL}/api/expenses`);
      const nextExpenses = Array.isArray(response.data) ? response.data : [];
      setExpenses(nextExpenses);
      const storedTitles = getStoredExpenseTitles();
      const optionNames = [
        ...new Set([
          ...defaultTitleOptions,
          ...storedTitles.filter(Boolean),
          ...nextExpenses
            .map((item) => String(item.title || "").trim())
            .filter(Boolean),
        ]),
      ];
      setTitleOptions(optionNames);
    } catch (error) {
      console.error("Error fetching expenses", error);
      setExpenses([]);
      setFetchError(
        error.response?.status === 500
          ? "Could not load expense records. Run the database migration in the backend."
          : "Could not load expense records. Check that the API is running.",
      );
    } finally {
      setLoading(false);
    }
  };

  const filteredExpenses = filterVisible(
    expenses.filter((item) => {
      if (!filterTitle) {
        return true;
      }
      return String(item.title) === String(filterTitle);
    }),
    HIDDEN_KEYS.expenses,
  );
  void listVersion;

  const totalAmount = filteredExpenses.reduce(
    (sum, item) => sum + Number(item.amount ?? 0),
    0,
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setExpense({ ...expense, [name]: value });
  };

  const closeModal = () => {
    setExpense(defaultExpense());
    setShowModal(false);
  };

  const deleteExpense = (id) => {
    if (!window.confirm("Remove this expense from the list? Restore it from History."))
      return;
    hideItem(HIDDEN_KEYS.expenses, id);
    setListVersion((v) => v + 1);
  };

  const saveExpense = async (e) => {
    e.preventDefault();

    const title = expense.title?.trim();
    if (!title) {
      alert("Enter an expense title");
      return;
    }

    const amount = Number(expense.amount);
    if (!Number.isFinite(amount) || amount < 0.01) {
      alert("Enter an amount of at least 0.01");
      return;
    }

    const payload = {
      title,
      description: expense.description?.trim() || null,
      expense_date: expense.expense_date,
      amount,
    };

    try {
      await axios.post(`${API_BASE_URL}/api/expenses`, payload);
      alert("Expense record saved successfully");
      closeModal();
      fetchExpenses();
    } catch (error) {
      console.error("Error saving expense", error);
      const data = error.response?.data;
      const validationMsg = data?.errors
        ? Object.values(data.errors).flat().join(" ")
        : null;
      alert(
        validationMsg ||
          data?.message ||
          "Failed to save expense. Check the form and try again.",
      );
    }
  };

  if (loading) {
    return <Loader message="Loading expense recordsâ€¦" fullPage />;
  }

  return (
    <>
      <div className="container-fluid app-container">
        {fetchError ? (
          <div className="alert alert-warning mb-3" role="alert">
            {fetchError}
          </div>
        ) : null}
        <div className="row">
          <div className="col-12">
            <div className="card app-screen-card">
              <div className="app-sales-toolbar">
                <button
                  type="button"
                  className="btn btn-primary app-btn"
                  onClick={() => setShowModal(true)}
                >
                  + Add Expense
                </button>
                <Link to="/history" className="btn btn-outline-secondary app-btn ms-2">
                  History
                </Link>
                <div className="app-sales-filter">
                  <div className="mb-3">
                    <label className="form-label mb-1">Filter by title</label>
                    <select
                      className="form-select"
                      value={filterTitle}
                      onChange={(e) => setFilterTitle(e.target.value)}
                    >
                      <option value="">All titles</option>
                      {titleOptions.map((title) => (
                        <option key={title} value={title}>
                          {title}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="app-sales-stats small">
                  <strong>Entries:</strong> {filteredExpenses.length}
                  <span className="app-sales-stats__sep"> | </span>
                  <strong>Total:</strong> {totalAmount.toFixed(2)}
                </div>
              </div>
              <div className="table-responsive app-table-wrap">
                <table className="table table-sm app-data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Title</th>
                      <th>Amount</th>
                      <th>Description</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredExpenses.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-4">
                          No expense records yet.
                        </td>
                      </tr>
                    ) : (
                      filteredExpenses.map((item) => (
                        <tr key={item.id}>
                          <td data-label="Date">
                            {formatAppDate(item.expense_date)}
                          </td>
                          <td data-label="Title">{item.title}</td>
                          <td data-label="Amount" className="cell-highlight">
                            {Number(item.amount ?? 0).toFixed(2)}
                          </td>
                          <td data-label="Description">
                            {item.description || "-"}
                          </td>
                          <td data-label="Action">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => deleteExpense(item.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div
          className="app-modal-overlay"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div
            className="app-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="expense-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="app-modal__header">
              <h4 className="app-modal__title" id="expense-modal-title">
                Add Expense
              </h4>
              <button
                type="button"
                className="app-modal__close"
                aria-label="Close"
                onClick={closeModal}
              >
               X
              </button>
            </div>
            <form onSubmit={saveExpense}>
              <div className="app-modal__body">
                <div className="mb-3">
                  <label className="form-label">Title</label>
                  <select
                    className="form-select"
                    name="title"
                    value={expense.title}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select title</option>
                    {titleOptions.map((title) => (
                      <option key={title} value={title}>
                        {title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Date</label>
                  <input
                    className="form-control"
                    type="date"
                    name="expense_date"
                    value={expense.expense_date}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Amount</label>
                  <input
                    className="form-control"
                    type="number"
                    name="amount"
                    value={expense.amount}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={expense.description}
                    onChange={handleChange}
                    placeholder="Optional notes"
                    rows={3}
                  />
                </div>
              </div>
              <div className="app-modal__footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary ms-2">
                  Save Expense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
